import { isSameDay } from "./appointments";

/**
 * buildSystemAlerts — pure function that returns a list of smart alerts for
 * the salon dashboard. Shared between Home (mural at the top of the bento)
 * and the dedicated Avisos module + Sidebar counter badge.
 */
export const buildSystemAlerts = ({ products, clients, appointments, whatsapp, subscriptionsSummary, waitlistSummary, aiUsage }) => {
    const items = [];
    const today0 = new Date();
    today0.setHours(0, 0, 0, 0);
    const todayTs = today0.getTime();

    // 0. WhatsApp desconectado — só quando dados de status carregados (não `loading`)
    if (whatsapp && whatsapp.loading === false && whatsapp.connected === false) {
        items.push({
            id: "whatsapp-disconnected",
            kind: "warning",
            icon: "fa-whatsapp",
            iconStyle: "brand",
            iconColor: "#25D366",
            title: "WhatsApp desconectado",
            detail: "As mensagens automáticas (lembretes, confirmações e campanhas) estão pausadas.",
            actionLabel: "Conectar agora",
            target: "whatsapp",
        });
    }

    // 1. Estoque baixo (stock <= minStock)
    const lowStock = (products || []).filter((p) => p.active && p.stock <= p.minStock);
    if (lowStock.length > 0) {
        const critical = lowStock.filter((p) => p.stock <= 2);
        items.push({
            id: "stock-low",
            kind: critical.length > 0 ? "critical" : "warning",
            icon: "fa-box-open",
            title: `${lowStock.length} produto${lowStock.length > 1 ? "s" : ""} com estoque baixo`,
            detail: critical.length > 0
                ? `${critical[0].name} está em ${critical[0].stock} unidade${critical[0].stock !== 1 ? "s" : ""} · crítico`
                : `${lowStock[0].name} · ${lowStock[0].stock} restante${lowStock[0].stock !== 1 ? "s" : ""}`,
            actionLabel: "Repor agora",
            target: "catalog",
        });
    }

    // 2. Aniversariantes esta semana
    const upcomingBirthdays = (clients || []).filter((c) => {
        if (!c.birthDate) return false;
        const [, m, d] = c.birthDate.split("-").map(Number);
        if (!m || !d) return false;
        const bday = new Date(today0.getFullYear(), m - 1, d);
        const diff = bday.getTime() - todayTs;
        const altBday = new Date(today0.getFullYear() + 1, m - 1, d);
        const diffAlt = altBday.getTime() - todayTs;
        const days = Math.min(
            diff >= 0 ? Math.floor(diff / 86400000) : 999,
            diffAlt >= 0 ? Math.floor(diffAlt / 86400000) : 999
        );
        return days >= 0 && days <= 7;
    });
    if (upcomingBirthdays.length > 0) {
        const first = upcomingBirthdays[0];
        items.push({
            id: "birthdays",
            kind: "info",
            icon: "fa-cake-candles",
            title: `${upcomingBirthdays.length} aniversariante${upcomingBirthdays.length > 1 ? "s" : ""} esta semana`,
            detail: `${first.name} faz aniversário em breve · ideal para um cupom`,
            actionLabel: "Ver clientes",
            target: "clients",
            whatsappClient: first,
            whatsappTemplateId: "tpl-aniversario",
            whatsappContext: { servico_nome: "barba" },
        });
    }

    // 3. Clientes inativos (>= 90 dias)
    const inactiveCutoff = todayTs - 90 * 86400000;
    const inactive = (clients || []).filter((c) => {
        const txns = c.transactions || [];
        if (txns.length === 0) return false;
        const last = Math.max(...txns.map((t) => t.date || 0));
        return last > 0 && last < inactiveCutoff;
    });
    if (inactive.length > 0) {
        const sorted = inactive
            .map((c) => ({
                ...c,
                daysAway: Math.floor(
                    (todayTs - Math.max(...(c.transactions || []).map((t) => t.date || 0))) / 86400000
                ),
            }))
            .sort((a, b) => b.daysAway - a.daysAway);
        items.push({
            id: "inactive",
            kind: "warning",
            icon: "fa-user-clock",
            title: `${inactive.length} cliente${inactive.length > 1 ? "s" : ""} há +90 dias sem voltar`,
            detail: `${sorted[0].name} há ${sorted[0].daysAway} dias · vale uma mensagem`,
            actionLabel: "Reativar",
            target: "clients",
            whatsappClient: sorted[0],
            whatsappTemplateId: "tpl-reativacao-corte",
            whatsappContext: { dias_inativo: sorted[0].daysAway },
        });
    }

    // 4. Faturamento hoje × mesmo dia semana passada (delta >= 10%)
    const sameDayLastWeek = new Date(today0);
    sameDayLastWeek.setDate(sameDayLastWeek.getDate() - 7);
    const todayRev = appointments
        .filter((a) => a.status === "concluido" && isSameDay(a.date, today0))
        .reduce((s, a) => s + (a.servicePrice || 0), 0);
    const lastWeekRev = appointments
        .filter((a) => a.status === "concluido" && isSameDay(a.date, sameDayLastWeek))
        .reduce((s, a) => s + (a.servicePrice || 0), 0);
    if (lastWeekRev > 0) {
        const delta = ((todayRev - lastWeekRev) / lastWeekRev) * 100;
        const absDelta = Math.abs(delta);
        if (absDelta >= 10) {
            const weekdayName = today0.toLocaleDateString("pt-BR", { weekday: "long" });
            items.push({
                id: "revenue-trend",
                kind: delta > 0 ? "success" : "warning",
                icon: delta > 0 ? "fa-arrow-trend-up" : "fa-arrow-trend-down",
                title: `Faturamento ${delta > 0 ? "+" : ""}${delta.toFixed(0)}% vs ${weekdayName} passad${weekdayName.endsWith("a") ? "a" : "o"}`,
                detail: `Hoje R$ ${todayRev.toFixed(2)} · semana passada R$ ${lastWeekRev.toFixed(2)}`,
                actionLabel: "Ver detalhes",
                target: "admin",
            });
        }
    }

    // 5. Serviços em andamento atrasados
    const overdue = appointments.filter((a) => {
        if (a.status !== "em-andamento" || !a.started_at) return false;
        const elapsed = Date.now() - a.started_at;
        const expected = (a.duration_min || 30) * 60000;
        return elapsed > expected * 1.1;
    });
    if (overdue.length > 0) {
        const first = overdue[0];
        const elapsedMin = Math.round((Date.now() - first.started_at) / 60000);
        const overMin = elapsedMin - (first.duration_min || 30);
        items.push({
            id: "overdue",
            kind: "critical",
            icon: "fa-triangle-exclamation",
            title: `${overdue.length} serviço${overdue.length > 1 ? "s" : ""} atrasado${overdue.length > 1 ? "s" : ""}`,
            detail: `${first.clientName} com ${first.professionalName} · +${overMin} min`,
            actionLabel: "Ver agenda",
            target: "agenda",
            targetView: "grid", // pula o hub da Agenda — cai direto na grade
        });
    }

    // 6. Assinaturas do Clube — vencendo em 7 dias / vencidas sem renovação
    if (subscriptionsSummary && subscriptionsSummary.expiring_soon > 0) {
        items.push({
            id: "subscription-expiring",
            kind: "warning",
            icon: "fa-medal",
            title: `${subscriptionsSummary.expiring_soon} assinante${subscriptionsSummary.expiring_soon > 1 ? "s" : ""} do Clube vencendo em 7 dias`,
            detail: "Renove antes que percam o acesso aos benefícios",
            actionLabel: "Ver assinantes",
            target: "subscriptions",
        });
    }
    if (subscriptionsSummary && subscriptionsSummary.expired > 0) {
        items.push({
            id: "subscription-expired",
            kind: "critical",
            icon: "fa-medal",
            title: `${subscriptionsSummary.expired} assinatura${subscriptionsSummary.expired > 1 ? "s" : ""} vencida${subscriptionsSummary.expired > 1 ? "s" : ""} sem renovação`,
            detail: "Entre em contato para renovar o plano",
            actionLabel: "Ver assinantes",
            target: "subscriptions",
        });
    }

    // 7. Lista de espera — clientes aguardando
    if (waitlistSummary && waitlistSummary.active > 0) {
        items.push({
            id: "waitlist-pending",
            kind: "info",
            icon: "fa-hourglass-half",
            title: `${waitlistSummary.active} cliente${waitlistSummary.active > 1 ? "s" : ""} na lista de espera`,
            detail: waitlistSummary.notified > 0
                ? `${waitlistSummary.notified} já notificado${waitlistSummary.notified > 1 ? "s" : ""} — aguardando resposta`
                : "Abra a Agenda para gerenciar a fila",
            actionLabel: "Abrir Agenda",
            target: "agenda",
            targetView: "waitlist", // pula o hub — grade com a Lista de Espera aberta
        });
    }

    // 8. Créditos do Agente IA quase esgotados (< 10% da quota mensal restante)
    if (aiUsage && aiUsage.quota_limit > 0) {
        const remaining = aiUsage.quota_limit - (aiUsage.tokens_used || 0);
        const remainingPct = (remaining / aiUsage.quota_limit) * 100;
        if (remainingPct < 10) {
            items.push({
                id: "ai-credits-low",
                kind: "warning",
                icon: "fa-robot",
                title: "Agente IA — créditos quase esgotados",
                detail: `Seu agente IA está com ${remainingPct.toFixed(1)}% de créditos restantes. Em breve o atendimento automático será pausado.`,
                actionLabel: "Recarregar créditos",
                href: "mailto:suporte@kinng.app",
            });
        }
    }

    return items;
};
